import { normalizeDesignLocationAndPoints } from '@shared/helpers/design/design.helpers';
import {
  createDesignBuilding,
  getBuildingById,
} from '@shared/helpers/design/storey.helpers';
import type {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { Storey } from '@shared/models/design/storey.model';
import type { Coordinate } from '@shared/models/geography.model';

const SELECTED_BUILDING_FALLBACK_ID = 'selected';

export type RebaseMapOriginInput = {
  location: Coordinate;
  selectedBuildingId: string | undefined;
  storeys: Storey[];
  otherBuildings: readonly Building[];
  blueprintUnderlays?: readonly BlueprintUnderlay[];
};

export type RebaseMapOriginResult = {
  location: Coordinate;
  storeys: Storey[];
  otherBuildings: Building[];
  blueprintUnderlays: BlueprintUnderlay[] | undefined;
  didChange: boolean;
};

export type MovedGeometryCommit = {
  /** Translated storeys to send to persist (pre-rebase). */
  persistStoreys: Storey[];
  /** Pin and footprints already matching persist normalize. */
  display: RebaseMapOriginResult;
};

export type DesignMapLocationStoreysTarget = {
  setLocation: (location: Coordinate) => void;
  setStoreys: (storeys: Storey[], options?: { buildingId?: string }) => void;
};

/**
 * Move the location pin to the combined footprint midpoint and rebase every
 * building so they stay put on the map. Matches persist
 * {@link normalizeDesignLocationAndPoints}.
 */
export const rebaseMapOriginToFootprintMidpoint = (
  input: RebaseMapOriginInput,
): RebaseMapOriginResult => {
  const selectedId = input.selectedBuildingId ?? SELECTED_BUILDING_FALLBACK_ID;
  const selected = createDesignBuilding(input.storeys, { id: selectedId });
  const others = input.otherBuildings.filter(
    (building) => building.id !== selectedId,
  );
  const design: BuildingDesign = {
    location: input.location,
    activities: [],
    buildings: [selected, ...others],
    ...(input.blueprintUnderlays?.length
      ? { blueprintUnderlays: [...input.blueprintUnderlays] }
      : {}),
  };
  const normalized = normalizeDesignLocationAndPoints(design);
  const selectedBuilding =
    getBuildingById(normalized, selectedId) ?? normalized.buildings[0];
  const otherBuildings = normalized.buildings.filter(
    (building) => building.id !== selectedBuilding?.id,
  );
  const didChange =
    normalized.location.lat !== input.location.lat ||
    normalized.location.lng !== input.location.lng;
  return {
    location: normalized.location,
    storeys: selectedBuilding?.storeys ?? input.storeys,
    otherBuildings,
    blueprintUnderlays: normalized.blueprintUnderlays,
    didChange,
  };
};

/**
 * Split a finished footprint move into persist geometry and local display.
 * Persist receives the translated storeys; the map already shows the pin at
 * the combined midpoint so save cannot apply origin and points on separate
 * frames. Passing {@link MovedGeometryCommit.display} storeys to persist with
 * the pre-move pin would skip normalize and leave the pin behind.
 */
export const resolveMovedGeometryCommit = (
  input: RebaseMapOriginInput,
): MovedGeometryCommit => ({
  persistStoreys: input.storeys,
  display: rebaseMapOriginToFootprintMidpoint(input),
});

/**
 * Push origin and footprints in one step so a new pin is never painted
 * against unrebased storeys.
 * Skips {@link DesignMapLocationStoreysTarget.setLocation} when location is
 * omitted.
 */
export const applyDesignMapLocationAndStoreys = (
  map: DesignMapLocationStoreysTarget,
  input: {
    location: Coordinate | undefined;
    storeys: Storey[];
    selectedBuildingId?: string;
  },
): void => {
  if (input.location) map.setLocation(input.location);
  map.setStoreys(input.storeys, { buildingId: input.selectedBuildingId });
};
